import dayjs from 'dayjs'
import { useAtom } from 'jotai'
import { useSetAtom } from 'jotai'
import { useCallback, useEffect } from 'react'
import { useParams } from 'react-router'

import { useUserManagement } from './useUserManagement'
import { instructorAtom, selectedInstructorIdAtom, availableSessionsAtom } from '../../contexts/AtomPrivateSessionSearch'
import { InstructorViewAtom } from '../../contexts/StatePageScope'
import GroupSessionService from '../../services/GroupSessionService'
import PrivateSessionService from '../../services/PrivateSessionService'
import useBar from '../common/Bar'

/**
 * 自定义hook：处理教练详情页面的数据和状态
 * 将业务逻辑与UI渲染分离
 */
export default function useInstructorView() {
  const params = useParams()
  const instructorId = params.id
  const { fetchUser } = useUserManagement()
  const bar = useBar()

  // const [instructor, setInstructor] = useState(null)
  const [groupSessions, setGroupSessions] = useAtom(InstructorViewAtom.groupSessions)
  const [privateSessions, setPrivateSessions] = useAtom(InstructorViewAtom.privateSessions)
  const [loading, setLoading] = useAtom(InstructorViewAtom.loading)
  const [error, setError] = useAtom(InstructorViewAtom.error)

  const setSelectedInstructorId = useSetAtom(selectedInstructorIdAtom)
  const setAvailableSessionsState = useSetAtom(availableSessionsAtom)
  const [instructor, setInstructor] = useAtom(instructorAtom)

  // 加载教练信息
  const loadInstructor = useCallback(async () => {
    try {
      const userData = await fetchUser({ userId: instructorId })
      setInstructor(userData)
      setSelectedInstructorId(userData.resourceId)
    } catch (error) {
      console.error('Error loading instructor:', error)
      setError('Failed to load instructor data')
      bar.error('Failed to load instructor data')
    }
  }, [])

  // 加载教练的团体课程
  const loadGroupSessions = useCallback(async () => {
    try {
      const response = await GroupSessionService.getGroupSessions({
        instructorId: instructorId,
        size: -1,
        dateAfter: dayjs().startOf('day').valueOf(),
      })
      setGroupSessions(response)
    } catch (error) {
      console.error('Error loading sessions:', error)
      bar.error('无法加载团体课程信息')
    }
  }, [])

  const loadUpcomingPrivateSessions = useCallback(async (instructorId) => {
    try {
      const filters = {
        instructorId: instructorId,
        status: 'AVAILABLE',
        size: -1,
        dateAfter: dayjs().valueOf(),
      }
      const privateSessions = await PrivateSessionService.getPrivateSessions(filters)
      // TODO Update this
      setPrivateSessions(privateSessions)
      setAvailableSessionsState(privateSessions)
    } catch (err) {
      console.error('Error fetching instructor sessions:', err)
    }
  }, [])

  const reload = useCallback(async () => {
    console.log('Calling reload')
    setError(null)
    await loadGroupSessions()
    await loadUpcomingPrivateSessions(instructorId)
  }, [])

  // 页面加载时获取数据
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      setError(null)
      try {
        await Promise.all([loadInstructor(), loadGroupSessions(), loadUpcomingPrivateSessions(instructorId)])
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [instructorId])
  // }, [loadUpcomingPrivateSessions, instructorId, setLoading, setError, loadGroupSessions, loadInstructor])

  return {
    instructor, // TODO Remove this
    groupSessions,
    privateSessions,
    reload,
    loading,
    error,
  }
}
